import { ComplianceDashboardData } from '@/types'

/** Prefer adjudication_category; fall back to legacy adjudication_cat (pre-migration silver). */
export function adjudicationCategoryValue(record: ComplianceDashboardData): string {
  const v = record.adjudication_category ?? record.adjudication_cat
  return v != null && v !== '' ? String(v) : ''
}

export function normalizeDecision(decision: string | null | undefined): string {
  if (!decision || typeof decision !== 'string') return 'UNKNOWN'

  try {
    const normalized = decision.toUpperCase().trim()

    if (normalized.includes('REPORTABLE') && !normalized.includes('NOT')) {
      return 'REPORTABLE'
    }
    if (normalized.includes('NOT_REPORTABLE') || normalized.includes('NON-REPORTABLE') || normalized.includes('NOT REPORTABLE')) {
      return 'NOT_REPORTABLE'
    }
    if (normalized.includes('MANUAL') || normalized.includes('REVIEW')) {
      return 'MANUAL_REVIEW'
    }

    return 'UNKNOWN'
  } catch {
    return 'UNKNOWN'
  }
}

export function normalizeGroundTruth(groundTruth: string | null | undefined): string {
  if (!groundTruth || typeof groundTruth !== 'string') return 'UNKNOWN'

  try {
    const normalized = groundTruth.toUpperCase().trim()

    // Check for NOT_REPORTABLE first (before checking for REPORTABLE)
    if (normalized.includes('NOT') || normalized.includes('NON')) {
      return 'NOT_REPORTABLE'
    }

    // Then check for REPORTABLE
    if (normalized.includes('REPORTABLE') || normalized === 'R') {
      return 'REPORTABLE'
    }

    // Handle CLEAR as NOT_REPORTABLE
    if (normalized.includes('CLEAR')) {
      return 'NOT_REPORTABLE'
    }

    return 'NOT_REPORTABLE' // Default to NOT_REPORTABLE if unclear (matching Streamlit)
  } catch {
    return 'UNKNOWN'
  }
}

export function calculateMatch(ceDecision: string, groundTruth: string): 'MATCH' | 'MISMATCH' | 'MANUAL_REVIEW' | 'UNKNOWN' {
  const normalizedCE = normalizeDecision(ceDecision)
  const normalizedGT = normalizeGroundTruth(groundTruth)

  if (normalizedCE === 'UNKNOWN' || normalizedGT === 'UNKNOWN') {
    return 'UNKNOWN'
  }

  if (normalizedCE === 'MANUAL_REVIEW') {
    return 'MANUAL_REVIEW'
  }

  return normalizedCE === normalizedGT ? 'MATCH' : 'MISMATCH'
}

export function getErrorType(ceDecision: string, groundTruth: string): 'FALSE_POSITIVE' | 'FALSE_NEGATIVE' | 'CORRECT' | 'OTHER' {
  const normalizedCE = normalizeDecision(ceDecision)
  const normalizedGT = normalizeGroundTruth(groundTruth)

  if (normalizedCE === 'UNKNOWN' || normalizedGT === 'UNKNOWN' || normalizedCE === 'MANUAL_REVIEW') {
    return 'OTHER'
  }

  if (normalizedCE === normalizedGT) {
    return 'CORRECT'
  }

  if (normalizedCE === 'REPORTABLE' && normalizedGT === 'NOT_REPORTABLE') {
    return 'FALSE_POSITIVE'
  }

  if (normalizedCE === 'NOT_REPORTABLE' && normalizedGT === 'REPORTABLE') {
    return 'FALSE_NEGATIVE'
  }

  return 'OTHER'
}

export function segmentOffense(record: ComplianceDashboardData): 'EVALUABLE' | 'REQUIRES_RESEARCH' | 'IGNORED_BY_UBS' {
  if (!record) return 'EVALUABLE'

  // If segment is already provided by the view, use it
  if (record.segment) {
    return record.segment as 'EVALUABLE' | 'REQUIRES_RESEARCH' | 'IGNORED_BY_UBS'
  }

  try {
    const notes = (record.all_notes || '').toString().toLowerCase()
    const dispClass = (record.disp_class || '').toString().toLowerCase()
    const adjudicationCat = adjudicationCategoryValue(record).toLowerCase()

    // Pattern definitions from Streamlit dashboard
    const ID_MISMATCH_PATTERNS = [
      'common name', 'no match', 'mismatch', 'alias',
      'dob', 'date of birth', '3rd id', 'need 3id',
      'wrong person', 'different person', 'not same',
      'identity', 'ssn'
    ]

    const DUPLICATE_PATTERNS = [
      'duplicate', 'same case', 'dup', 'dupe', 'clear dup',
      'already reported', 'reported on', 'reported in',
      'see sep', 'see other', 'refer to', 'cw', 'sw',
      'search for results'
    ]

    // Cross-search reference numbers like 12345678.123456789 indicate the
    // result lives on a different search → duplicate
    const DUPLICATE_REGEXES = [/\d{8}\.\d{9}/]

    const JAIL_PATTERNS = ['jail', 'prison', 'incarcerat', 'custody', 'sentenced to', 'confinement']

    // Check for IGNORED_BY_UBS (ID mismatch / data issues)
    for (const pattern of ID_MISMATCH_PATTERNS) {
      if (notes.includes(pattern)) {
        return 'IGNORED_BY_UBS'
      }
    }

    for (const pattern of DUPLICATE_PATTERNS) {
      if (notes.includes(pattern)) {
        return 'IGNORED_BY_UBS'
      }
    }

    for (const re of DUPLICATE_REGEXES) {
      if (re.test(notes)) {
        return 'IGNORED_BY_UBS'
      }
    }

    // Check for REQUIRES_RESEARCH
    // Check if adjudication category contains "Review Required"
    if (adjudicationCat.includes('review required')) {
      return 'REQUIRES_RESEARCH'
    }

    // Check if disposition class is "In Progress"
    if (dispClass === 'in progress') {
      return 'REQUIRES_RESEARCH'
    }

    // Check for jail-related cases
    for (const pattern of JAIL_PATTERNS) {
      if (notes.includes(pattern)) {
        return 'REQUIRES_RESEARCH'
      }
    }

    // Default to evaluable
    return 'EVALUABLE'
  } catch {
    return 'EVALUABLE'
  }
}

export function formatDate(dateString: string): string {
  if (!dateString) return 'N/A'

  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return dateString
  }
}

export function calculateStatistics(data: ComplianceDashboardData[]) {
  if (!data || !Array.isArray(data)) {
    return {
      totalOffenses: 0,
      evaluable: 0,
      requiresResearch: 0,
      ignoredByUbs: 0,
      accuracy: 0,
      precision: 0,
      recall: 0,
      f1Score: 0,
      truePositives: 0,
      trueNegatives: 0,
      falsePositives: 0,
      falseNegatives: 0,
    }
  }

  // Filter to EVALUABLE segment only
  const evaluableData = data.filter(record => segmentOffense(record) === 'EVALUABLE')

  let truePositives = 0
  let trueNegatives = 0
  let falsePositives = 0
  let falseNegatives = 0

  // Use match_status and error_type from the view if available, otherwise calculate
  evaluableData.forEach(record => {
    const errorType = record.error_type || getErrorType(record.ce_decision, record.ground_truth)
    const normalizedGT = normalizeGroundTruth(record.ground_truth)

    if (errorType === 'CORRECT') {
      // Split CORRECT into TP and TN based on ground truth
      if (normalizedGT === 'REPORTABLE') {
        truePositives++
      } else if (normalizedGT === 'NOT_REPORTABLE') {
        trueNegatives++
      }
    } else if (errorType === 'FALSE_POSITIVE') {
      falsePositives++
    } else if (errorType === 'FALSE_NEGATIVE') {
      falseNegatives++
    }
    // Skip 'OTHER' (UNKNOWN/MANUAL_REVIEW cases)
  })

  // Calculate metrics (only on records with known decisions)
  const total = truePositives + trueNegatives + falsePositives + falseNegatives
  const accuracy = total > 0 ? ((truePositives + trueNegatives) / total) * 100 : 0

  const precision = (truePositives + falsePositives) > 0
    ? (truePositives / (truePositives + falsePositives)) * 100
    : 0

  const recall = (truePositives + falseNegatives) > 0
    ? (truePositives / (truePositives + falseNegatives)) * 100
    : 0

  const f1Score = (precision + recall) > 0
    ? (2 * precision * recall) / (precision + recall)
    : 0

  return {
    totalOffenses: data.length,
    evaluable: evaluableData.length,
    requiresResearch: data.filter(r => segmentOffense(r) === 'REQUIRES_RESEARCH').length,
    ignoredByUbs: data.filter(r => segmentOffense(r) === 'IGNORED_BY_UBS').length,
    accuracy,
    precision,
    recall,
    f1Score,
    truePositives,
    trueNegatives,
    falsePositives,
    falseNegatives,
  }
}
